import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Icon,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../modules/auth/hooks/useAuth";
import {
  isModuleAdmin,
  hasPermission,
} from "../../modules/auth/utils/permission.utils";

const TRANSITION_DURATION = 0.2;
const HOVER_ANIMATION = {
  transition: `all ${TRANSITION_DURATION}s cubic-bezier(0.4, 0, 0.2, 1)`,
};

// Generic menu item interface
export interface UnifiedMenuItem {
  id: string;
  text: string;
  icon?: string;
  path: string;
  permission?: string; // Single permission required to access this item
  requiresModuleAdmin?: boolean; // Special flag for configuration routes that require module admin
  subItems?: UnifiedMenuItem[];
}

// Configuration for the sidebar
export interface SidebarConfig {
  logo: string;
  logoAlt: string;
  moduleName?: string; // For module admin checking (e.g., "accounting", "business")
  defaultWidth: number;
  collapsedWidth?: number;
  menuItems: UnifiedMenuItem[];
  autoExpandPaths?: { [menuId: string]: string }; // Auto-expand menu items based on current path
}

// Props for the unified sidebar
export interface UnifiedSidebarProps {
  config: SidebarConfig;
  currentPath: string;
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  onLogout: () => void;
  className?: string;
}

// Helper function to check if user has permission for a menu item
const hasMenuItemPermission = (
  item: UnifiedMenuItem,
  permissions: any,
  location_id: string,
  isStoreOwner: boolean = false,
  isModuleAdmin: boolean = false
): boolean => {
  // Store owners have access to everything
  if (isStoreOwner) {
    return true;
  }

  // If item requires module admin access
  if (item.requiresModuleAdmin) {
    return isModuleAdmin;
  }

  // If item has sub-items (is a collapsible group), check if user has permission to ANY child
  if (item.subItems && item.subItems.length > 0) {
    const hasAccessToAnyChild = item.subItems.some((subItem) =>
      hasMenuItemPermission(
        subItem,
        permissions,
        location_id,
        isStoreOwner,
        isModuleAdmin
      )
    );

    // Show the group if user has access to at least one child
    if (hasAccessToAnyChild) {
      return true;
    }
  }

  // If item has no permission requirement, it's accessible to everyone
  if (!item.permission) {
    return true;
  }

  // Check if user has the required permission for this specific item
  return hasPermission(item.permission, permissions, location_id, isStoreOwner);
};

// Function to generate visible menu items based on permissions
const generateVisibleMenuItems = (
  menuItems: UnifiedMenuItem[],
  permissions: any,
  location_id: string,
  isStoreOwner: boolean = false,
  isModuleAdmin: boolean = false
): UnifiedMenuItem[] => {
  return menuItems
    .filter((item) =>
      hasMenuItemPermission(
        item,
        permissions,
        location_id,
        isStoreOwner,
        isModuleAdmin
      )
    )
    .map((item): UnifiedMenuItem => {
      const menuItem: UnifiedMenuItem = {
        id: item.id,
        text: item.text,
        icon: item.icon,
        path: item.path,
        permission: item.permission,
        requiresModuleAdmin: item.requiresModuleAdmin,
      };

      // Process sub items if they exist
      if (item.subItems) {
        menuItem.subItems = item.subItems
          .filter((subItem) =>
            hasMenuItemPermission(
              subItem,
              permissions,
              location_id,
              isStoreOwner,
              isModuleAdmin
            )
          )
          .map(
            (subItem): UnifiedMenuItem => ({
              id: subItem.id,
              text: subItem.text,
              icon: subItem.icon,
              path: subItem.path,
              permission: subItem.permission,
              requiresModuleAdmin: subItem.requiresModuleAdmin,
            })
          );
      }

      return menuItem;
    });
};

export const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  config,
  currentPath,
  mobileOpen,
  onDrawerToggle,
  onLogout,
  className,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserModuleAdmin, setIsUserModuleAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    const expanded: string[] = [];

    // Auto-expand menu items based on current path
    if (config.autoExpandPaths) {
      Object.entries(config.autoExpandPaths).forEach(([menuId, pathPrefix]) => {
        if (currentPath.startsWith(pathPrefix)) {
          expanded.push(menuId);
        }
      });
    }

    return expanded;
  });

  const collapsedWidth = config.collapsedWidth || 72;
  const expandedWidth = config.defaultWidth;

  // Check if user is module admin - memoized with useCallback
  const checkModuleAdmin = () => {
    const storeId = user?.store_id;

    if (config.moduleName && user?._id && storeId) {
      setAdminCheckLoading(true);
      isModuleAdmin(
        user._id,
        storeId,
        config.moduleName as any,
        user.is_store_owner || false
      )
        .then((isAdmin: boolean) => {
          setIsUserModuleAdmin(isAdmin);
        })
        .catch((error: any) => {
          console.error(
            `Error checking ${config.moduleName} admin status:`,
            error
          );
          setIsUserModuleAdmin(false);
        })
        .finally(() => {
          setAdminCheckLoading(false);
        });
    } else {
      setIsUserModuleAdmin(false);
      setAdminCheckLoading(false);
    }
  };

  useEffect(() => {
    if (config.moduleName) {
      checkModuleAdmin();
    } else {
      setAdminCheckLoading(false);
    }
  }, [user, config.moduleName]);

  // Generate menu items based on permissions
  const visibleMenuItems = React.useMemo(
    () =>
      generateVisibleMenuItems(
        config.menuItems,
        user?.newPermissions,
        user?.assignment_details?.id || "",
        user?.is_store_owner || false,
        isUserModuleAdmin
      ),
    [
      config.menuItems,
      user?.newPermissions,
      user?.assignment_details?.id,
      user?.is_store_owner,
      isUserModuleAdmin,
    ]
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isItemExpanded = (itemId: string) => expandedItems.includes(itemId);

  const renderMenuItem = (item: UnifiedMenuItem, isSubItem = false) => {
    const isSelected = currentPath === item.path;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = isItemExpanded(item.id);

    return (
      <React.Fragment key={item.id}>
        <ListItem
          onClick={() => {
            if (hasSubItems && !isCollapsed) {
              handleToggleExpand(item.id);
            } else {
              handleNavigation(item.path);
            }
          }}
          sx={{
            position: "relative",
            cursor: "pointer",
            mb: 0.5,
            py: isSubItem ? 1 : 1.5,
            px: isSubItem ? 3 : 2,
            ml: isSubItem ? 2 : 0,
            borderRadius: 1,
            color: isSelected ? "#111827" : "#6B7280",
            bgcolor: isSelected ? "rgba(0, 0, 0, 0.03)" : "transparent",
            minHeight: isCollapsed ? 48 : "auto",
            justifyContent: isCollapsed ? "center" : "flex-start",
            ...HOVER_ANIMATION,
            "&:hover": {
              color: "#111827",
              bgcolor: "rgba(0, 0, 0, 0.02)",
              "& .menu-icon": {
                color: "#111827",
              },
            },
          }}
        >
          <Tooltip title={isCollapsed ? item.text : ""} placement="right" arrow>
            <ListItemIcon
              sx={{
                minWidth: isCollapsed ? 0 : 36,
                color: "inherit",
                justifyContent: "center",
              }}
            >
              <Icon
                className="menu-icon"
                sx={{
                  fontSize: 20,
                  ...HOVER_ANIMATION,
                }}
              >
                {item.icon}
              </Icon>
            </ListItemIcon>
          </Tooltip>

          {!isCollapsed && (
            <>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: isSubItem ? "0.8125rem" : "0.875rem",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {item.text}
                  </Typography>
                }
              />
              {hasSubItems && (
                <Icon
                  sx={{
                    fontSize: 18,
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    ...HOVER_ANIMATION,
                  }}
                >
                  expand_more
                </Icon>
              )}
            </>
          )}
        </ListItem>

        {/* Sub Items */}
        {hasSubItems && !isCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems!.map((subItem) => renderMenuItem(subItem, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        borderRight: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.06)",
        width: isCollapsed ? collapsedWidth : expandedWidth,
        ...HOVER_ANIMATION,
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          p: isCollapsed ? 1.5 : 3,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
        }}
      >
        {!isCollapsed && (
          <Box
            sx={{
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1,
              overflow: "hidden",
              ...HOVER_ANIMATION,
            }}
          >
            <img
              src={config.logo}
              alt={config.logoAlt}
              style={{
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        <Tooltip
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          placement="right"
          arrow
        >
          <IconButton
            onClick={handleToggleCollapse}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": {
                color: "#111827",
                bgcolor: "rgba(0, 0, 0, 0.02)",
              },
            }}
          >
            <Icon sx={{ fontSize: 20 }}>
              {isCollapsed ? "chevron_right" : "chevron_left"}
            </Icon>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation Menu */}
      <List
        sx={{
          px: isCollapsed ? 1 : 2,
          py: 3,
          flex: 1,
        }}
      >
        {adminCheckLoading && config.moduleName ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          visibleMenuItems.map((item) => renderMenuItem(item))
        )}
      </List>

      {/* Sidebar Footer */}
      <Box
        sx={{
          p: isCollapsed ? 1.5 : 3,
          borderTop: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
        }}
      >
        <Tooltip title={isCollapsed ? "Sign Out" : ""} placement="right" arrow>
          <Button
            fullWidth={!isCollapsed}
            onClick={onLogout}
            variant="text"
            size="large"
            startIcon={!isCollapsed ? <Icon>logout</Icon> : undefined}
            sx={{
              py: 1.5,
              color: "#6B7280",
              fontSize: "0.875rem",
              fontWeight: 500,
              letterSpacing: "0.01em",
              justifyContent: isCollapsed ? "center" : "flex-start",
              minWidth: isCollapsed ? 40 : "auto",
              ...HOVER_ANIMATION,
              "&:hover": {
                color: "#111827",
                bgcolor: "rgba(0, 0, 0, 0.02)",
              },
            }}
          >
            {isCollapsed ? <Icon>logout</Icon> : "Sign Out"}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      className={className}
      sx={{
        width: { sm: isCollapsed ? collapsedWidth : expandedWidth },
        flexShrink: { sm: 0 },
        ...HOVER_ANIMATION,
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: expandedWidth,
            background: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: isCollapsed ? collapsedWidth : expandedWidth,
            background: "#ffffff",
            boxShadow: "none",
            ...HOVER_ANIMATION,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};
